/* ==========================================================================
   FLC Forge — Product Form & Variant Selects
   ========================================================================== */

class VariantSelects extends HTMLElement {
  connectedCallback() {
    this.productHandle = this.dataset.productHandle;
    this.productJSON   = JSON.parse(document.querySelector(`[data-product-json="${this.productHandle}"]`)?.textContent || '{}');
    this.inputs        = this.querySelectorAll('select[name^="options"], input[name^="options"]');

    this.inputs.forEach(input => {
      input.addEventListener('change', () => this.updateVariant());
    });

    // Init from URL or first available
    this.setFromURL();
  }

  get currentOptions() {
    return [...this.inputs].map(input => {
      return input.type === 'radio' ? this.querySelector(`input[name="${input.name}"]:checked`)?.value : input.value;
    }).filter(Boolean);
  }

  findVariant(options) {
    return this.productJSON.variants?.find(v =>
      v.options.every((opt, i) => opt === options[i])
    );
  }

  updateVariant() {
    const options = this.currentOptions;
    const variant = this.findVariant(options);

    document.querySelectorAll('[data-variant-id]').forEach(el => {
      el.value = variant?.id || '';
    });

    this.updatePrices(variant);
    this.updateAvailability(variant);
    this.updateMedia(variant);
    this.updateURL(variant);

    FLC.dispatch('flc:variant:changed', { variant, options }, this);
  }

  updatePrices(variant) {
    const priceEl   = document.querySelector('[data-product-price]');
    const compareEl = document.querySelector('[data-product-compare-price]');
    const priceWrap = document.querySelector('[data-price-wrap]');
    const btnPrice  = document.querySelector('[data-add-to-cart-price]');

    if (!variant) return;

    if (priceEl)   priceEl.innerHTML   = FLC.formatMoney(variant.price);
    if (compareEl) compareEl.innerHTML = variant.compare_at_price > variant.price
      ? FLC.formatMoney(variant.compare_at_price)
      : '';

    if (btnPrice) {
      btnPrice.dataset.unitPrice = variant.price;
      const qty = parseInt(document.querySelector('[data-form-quantity]')?.value || '1', 10);
      btnPrice.textContent = FLC.formatMoney(variant.price * qty);
    }

    priceWrap?.classList.toggle('price--on-sale', variant.compare_at_price > variant.price);
  }

  updateAvailability(variant) {
    const addBtn  = document.querySelector('[data-add-to-cart-btn]');
    const btnText = document.querySelector('[data-add-to-cart-text]');

    if (!addBtn) return;

    if (!variant) {
      addBtn.disabled = true;
      if (btnText) btnText.textContent = window.flcForge.i18n?.unavailable || 'Unavailable';
    } else if (!variant.available) {
      addBtn.disabled = true;
      if (btnText) btnText.textContent = window.flcForge.i18n?.sold_out || 'Sold Out';
    } else {
      addBtn.disabled = false;
      if (btnText) btnText.textContent = window.flcForge.i18n?.add_to_cart || 'Add to Cart';
    }
  }

  updateMedia(variant) {
    if (!variant?.featured_media) return;
    const mediaId = variant.featured_media.id;
    const mediaEl = document.querySelector(`[data-media-id="${mediaId}"]`);
    if (mediaEl) {
      // Scroll gallery to this media
      mediaEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }
  }

  updateURL(variant) {
    if (!variant) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  setFromURL() {
    const params  = new URLSearchParams(window.location.search);
    const varId   = params.get('variant');
    if (!varId) { this.updateVariant(); return; }

    const variant = this.productJSON.variants?.find(v => v.id == varId);
    if (!variant) { this.updateVariant(); return; }

    variant.options.forEach((opt, i) => {
      const input = this.inputs[i];
      if (!input) return;
      if (input.type === 'radio') {
        const radio = this.querySelector(`input[value="${CSS.escape(opt)}"]`);
        if (radio) radio.checked = true;
      } else {
        input.value = opt;
      }
    });

    this.updateVariant();
  }
}

customElements.define('variant-selects', VariantSelects);

/* Product Form (add to cart)
   ========================================================================== */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form       = this.querySelector('form');
    this.submitBtn  = this.querySelector('[data-add-to-cart-btn]');
    this.cartType   = window.flcForge.cartType || 'drawer';

    const jsonEl = document.querySelector('[data-product-json]');
    this.productJSON = JSON.parse(jsonEl?.textContent || '{}');

    this._lastPlanId = document.querySelector('[name="selling_plan"]')?.value || null;

    this.form?.addEventListener('submit', (e) => this.onSubmit(e));

    // Standard radio/select change (fires for real user interaction on the input itself)
    document.addEventListener('change', (e) => {
      if (e.target.name === 'selling_plan') this._onPlanChange(e.target.value);
    });

    // The Subscriptions app sets the hidden selling_plan input programmatically on card click,
    // which never fires a change event — poll for the new value after each click settles.
    document.addEventListener('click', () => {
      setTimeout(() => {
        const input = document.querySelector('[name="selling_plan"]');
        if (!input || input.value === this._lastPlanId) return;
        this._lastPlanId = input.value;
        this._onPlanChange(input.value);
      }, 0);
    });
  }

  _onPlanChange(planId) {
    for (const group of this.productJSON.selling_plan_groups || []) {
      for (const plan of group.selling_plans || []) {
        if (String(plan.id) !== String(planId)) continue;

        const adj = plan.price_adjustments?.[0];
        if (!adj) return;

        const variantId = this.form?.querySelector('[data-variant-id]')?.value;
        const variant   = this.productJSON.variants?.find(v => String(v.id) === String(variantId));
        const basePrice = variant?.price || 0;

        let price;
        if (adj.value_type === 'price') {
          price = adj.value;
        } else if (adj.value_type === 'percentage') {
          price = Math.round(basePrice * (1 - adj.value / 100));
        } else if (adj.value_type === 'fixed_amount') {
          price = basePrice - adj.value;
        }

        if (price == null) return;

        const priceEl  = document.querySelector('[data-product-price]');
        const btnPrice = document.querySelector('[data-add-to-cart-price]');
        if (priceEl) priceEl.innerHTML = FLC.formatMoney(price);
        if (btnPrice) {
          btnPrice.dataset.unitPrice = price;
          const qty = parseInt(document.querySelector('[data-form-quantity]')?.value || '1', 10);
          btnPrice.textContent = FLC.formatMoney(price * qty);
        }
        return;
      }
    }
  }

  async onSubmit(e) {
    e.preventDefault();
    this.setLoading(true);

    const formData  = new FormData(this.form);
    const variantId = formData.get('id');
    if (!variantId) return;

    // Also check DOM in case the app renders the input outside the form element
    const sellingPlan = formData.get('selling_plan') ||
      document.querySelector('[name="selling_plan"]:checked, input[name="selling_plan"][type="hidden"]')?.value;

    try {
      const body = { id: variantId, quantity: parseInt(formData.get('quantity') || '1', 10) };
      if (sellingPlan) body.selling_plan = parseInt(sellingPlan, 10);

      const data = await FLC.fetchJSON(window.flcForge.routes.cart_add_url, {
        method: 'POST',
        body: JSON.stringify(body)
      });

      FLC.dispatch('flc:cart:refresh');

      if (this.cartType === 'drawer') {
        FLC.dispatch('flc:cart:open');
      } else if (this.cartType === 'page') {
        window.location.href = window.flcForge.routes.cart_url;
      }

      window.liveRegion?.announce(window.flcForge.i18n?.added_to_cart || 'Added to cart');
    } catch (err) {
      console.error('Add to cart failed:', err);
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(state) {
    if (this.submitBtn) {
      this.submitBtn.disabled = state;
      this.submitBtn.setAttribute('aria-busy', state);
    }
  }
}

customElements.define('product-form', ProductForm);

/* Product Media Gallery
   ========================================================================== */
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.track       = this.querySelector('[data-gallery-track]');
    this.thumbBtns   = [...this.querySelectorAll('[data-gallery-thumb]')];
    this.prevBtn     = this.querySelector('[data-gallery-prev]');
    this.nextBtn     = this.querySelector('[data-gallery-next]');
    this.zoomOverlay = this.querySelector('[data-gallery-zoom]');
    this.zoomImg     = this.querySelector('[data-zoom-img]');
    this.prevZoomBtn = this.querySelector('[data-zoom-prev]');
    this.nextZoomBtn = this.querySelector('[data-zoom-next]');
    this.zoomSources = [...this.querySelectorAll('[data-zoom-src]')];

    this.currentIndex     = 0;
    this.currentZoomIndex = 0;
    this._zoomScale       = 1;
    this._zoomTx          = 0;
    this._zoomTy          = 0;
    this._isDragging      = false;
    this._wasDragging     = false;

    // Thumbnail clicks
    this.thumbBtns.forEach(btn => {
      btn.addEventListener('click', () => this.goTo(parseInt(btn.dataset.galleryThumb, 10)));
    });

    // Gallery arrow clicks
    this.prevBtn?.addEventListener('click', () => this.goTo(this.currentIndex - 1));
    this.nextBtn?.addEventListener('click', () => this.goTo(this.currentIndex + 1));

    // Sync on manual swipe/scroll
    if (this.track) {
      this.track.addEventListener('scroll', FLC.debounce(() => this._syncFromScroll(), 80), { passive: true });
    }

    // Zoom setup
    if ('zoomEnabled' in this.dataset) {
      this._initZoom();
    }

    // Variant change → jump to variant media
    document.addEventListener('flc:variant:changed', e => {
      if (e.detail.variant?.featured_media) {
        const mediaId = e.detail.variant.featured_media.id;
        const slides  = [...this.querySelectorAll('[data-media-id]')];
        const idx     = slides.findIndex(el => el.dataset.mediaId == mediaId);
        if (idx !== -1) this.goTo(idx);
      }
    });

    this._syncArrows(0);
  }

  /* Gallery navigation ---------------------------------------------------- */
  get _slideCount() { return this.track ? this.track.children.length : 0; }

  goTo(index) {
    if (!this.track) return;
    const i = Math.max(0, Math.min(index, this._slideCount - 1));
    this.track.children[i]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    this.currentIndex = i;
    this._syncThumbs(i);
    this._syncArrows(i);
  }

  _syncFromScroll() {
    if (!this.track || !this.track.clientWidth) return;
    const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
    if (i === this.currentIndex) return;
    this.currentIndex = i;
    this._syncThumbs(i);
    this._syncArrows(i);
  }

  _syncThumbs(i) {
    this.thumbBtns.forEach((btn, j) => {
      btn.classList.toggle('is-active', j === i);
      btn.setAttribute('aria-pressed', String(j === i));
    });
  }

  _syncArrows(i) {
    if (this.prevBtn) this.prevBtn.disabled = i === 0;
    if (this.nextBtn) this.nextBtn.disabled = i >= this._slideCount - 1;
  }

  /* Zoom ------------------------------------------------------------------ */
  _initZoom() {
    // Open zoom by clicking any zoomable media
    this.zoomSources.forEach((el, i) => {
      el.addEventListener('click', () => {
        if (!this._wasDragging) this._openZoom(i);
      });
    });

    // Close
    this.querySelector('[data-zoom-close]')?.addEventListener('click', () => this._closeZoom());
    this.zoomOverlay?.addEventListener('click', e => {
      if (e.target === this.zoomOverlay) this._closeZoom();
    });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.zoomOverlay?.getAttribute('aria-hidden') === 'false') this._closeZoom();
      if (e.key === 'ArrowLeft'  && this.zoomOverlay?.getAttribute('aria-hidden') === 'false') this._stepZoom(-1);
      if (e.key === 'ArrowRight' && this.zoomOverlay?.getAttribute('aria-hidden') === 'false') this._stepZoom(1);
    });

    // Zoom carousel arrows
    this.prevZoomBtn?.addEventListener('click', () => this._stepZoom(-1));
    this.nextZoomBtn?.addEventListener('click', () => this._stepZoom(1));

    // Click image to toggle zoom scale
    this.zoomImg?.addEventListener('click', e => {
      if (this._wasDragging) { this._wasDragging = false; return; }
      this._toggleScale(e);
    });

    // Drag to pan
    this._initDrag();
  }

  _openZoom(index) {
    const src = this.zoomSources[index];
    if (!src || !this.zoomImg) return;
    this._resetTransform();
    this.zoomImg.src    = src.dataset.zoomSrc;
    this.zoomImg.alt    = src.querySelector('img')?.alt || '';
    this.currentZoomIndex = index;
    this.zoomOverlay.setAttribute('aria-hidden', 'false');
    this._syncZoomNav(index);
    FLC.openOverlay();
  }

  _closeZoom() {
    if (!this.zoomOverlay) return;
    this._resetTransform();
    this.zoomOverlay.setAttribute('aria-hidden', 'true');
    this.zoomImg.src = '';
    FLC.closeOverlay();
  }

  _stepZoom(dir) {
    const next = Math.max(0, Math.min(this.zoomSources.length - 1, this.currentZoomIndex + dir));
    if (next === this.currentZoomIndex) return;
    const src = this.zoomSources[next];
    this._resetTransform();
    this.zoomImg.src    = src.dataset.zoomSrc;
    this.zoomImg.alt    = src.querySelector('img')?.alt || '';
    this.currentZoomIndex = next;
    this._syncZoomNav(next);
  }

  _syncZoomNav(i) {
    const total = this.zoomSources.length;
    if (total <= 1) { return; } // buttons are already hidden via HTML [hidden]
    this.prevZoomBtn?.removeAttribute('hidden');
    this.nextZoomBtn?.removeAttribute('hidden');
    if (this.prevZoomBtn) this.prevZoomBtn.disabled = i === 0;
    if (this.nextZoomBtn) this.nextZoomBtn.disabled = i >= total - 1;
  }

  /* Scale toggle (click to zoom in/out) ----------------------------------- */
  _toggleScale(e) {
    if (this._zoomScale > 1) {
      this._resetTransform();
      return;
    }
    const rect  = this.zoomImg.getBoundingClientRect();
    const s     = 2.5;
    // Keep the clicked point fixed: tx = (clickOffset - center) * (1 - s)
    this._zoomScale = s;
    this._zoomTx    = (e.clientX - rect.left - rect.width  / 2) * (1 - s);
    this._zoomTy    = (e.clientY - rect.top  - rect.height / 2) * (1 - s);
    this._applyTransform();
    this.zoomImg.style.cursor = 'grab';
  }

  _resetTransform() {
    this._zoomScale = 1;
    this._zoomTx    = 0;
    this._zoomTy    = 0;
    if (this.zoomImg) {
      this.zoomImg.style.transform = '';
      this.zoomImg.style.cursor    = '';
    }
  }

  _applyTransform() {
    if (this.zoomImg) {
      this.zoomImg.style.transform = `translate(${this._zoomTx}px,${this._zoomTy}px) scale(${this._zoomScale})`;
    }
  }

  /* Drag to pan ----------------------------------------------------------- */
  _initDrag() {
    const onStart = (cx, cy) => {
      if (this._zoomScale <= 1) return;
      this._isDragging  = true;
      this._wasDragging = false;
      this._dragX = cx - this._zoomTx;
      this._dragY = cy - this._zoomTy;
      if (this.zoomImg) this.zoomImg.style.cursor = 'grabbing';
    };

    const onMove = (cx, cy) => {
      if (!this._isDragging) return;
      this._wasDragging = true;
      this._zoomTx = cx - this._dragX;
      this._zoomTy = cy - this._dragY;
      this._applyTransform();
    };

    const onEnd = () => {
      if (!this._isDragging) return;
      this._isDragging = false;
      if (this.zoomImg) this.zoomImg.style.cursor = this._zoomScale > 1 ? 'grab' : '';
    };

    // Mouse
    this.zoomImg?.addEventListener('mousedown', e => { onStart(e.clientX, e.clientY); e.preventDefault(); });
    window.addEventListener('mousemove', e => onMove(e.clientX, e.clientY));
    window.addEventListener('mouseup', onEnd);

    // Touch
    this.zoomImg?.addEventListener('touchstart', e => {
      if (e.touches.length === 1) onStart(e.touches[0].clientX, e.touches[0].clientY);
    }, { passive: true });
    window.addEventListener('touchmove', e => {
      if (e.touches.length === 1) onMove(e.touches[0].clientX, e.touches[0].clientY);
    }, { passive: true });
    window.addEventListener('touchend', onEnd);
  }
}

customElements.define('product-gallery', ProductGallery);

/* Quantity Steppers
   ========================================================================== */
(function initQuantitySteppers() {
  function updateBtnPrice(qty) {
    const btnPrice = document.querySelector('[data-add-to-cart-price]');
    if (!btnPrice) return;
    const unitPrice = parseInt(btnPrice.dataset.unitPrice || '0', 10);
    if (!unitPrice) return;
    btnPrice.textContent = FLC.formatMoney(unitPrice * qty);
  }

  function syncQty(input) {
    const targetId = input.dataset.qtySync;
    if (!targetId) return;
    const target = document.getElementById(targetId);
    if (target) target.value = input.value;
    updateBtnPrice(parseInt(input.value || '1', 10));
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-quantity-change]');
    if (!btn) return;
    const wrap  = btn.closest('.product__qty-wrap');
    const input = wrap?.querySelector('input[type="number"]');
    if (!input) return;
    const delta = parseInt(btn.dataset.quantityChange, 10);
    const min   = parseInt(input.min || '1', 10);
    input.value = Math.max(min, parseInt(input.value || '1', 10) + delta);
    syncQty(input);
  });

  document.addEventListener('change', (e) => {
    if ('qtySync' in e.target.dataset) syncQty(e.target);
  });
})();
